import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { apiService } from '../services/api';
import type {
  AgentPR,
  AgentSummary,
  CycleLogEntry,
  EcosystemKPIs,
  EcosystemOpenIssue,
  EmailThread,
  FindingsDocContent,
  LoopConfig,
  StateMdResponse,
} from '../types/agents';

interface AgentsState {
  // Top-of-page glance
  overview: EcosystemKPIs | null;
  agents: AgentSummary[];
  issues: EcosystemOpenIssue[];
  prs: AgentPR[];
  emails: EmailThread[];
  stateMd: StateMdResponse | null;
  config: LoopConfig | null;

  // Lazy-loaded per-agent slices keyed by agent name
  cycleLogs: Record<string, CycleLogEntry[]>;
  findingsDocs: Record<string, FindingsDocContent>;

  // Loading flags
  loading: boolean;
  error: string | null;
  lastRefreshedAt: string | null;

  // Per-section in-flight tracking to avoid double-fires
  cycleLogLoading: Record<string, boolean>;
  findingsLoading: Record<string, boolean>;

  // Actions
  loadAll: () => Promise<void>;
  refresh: () => Promise<void>;
  loadCycleLog: (name: string) => Promise<void>;
  loadFindingsDoc: (filename: string) => Promise<void>;
}

export const useAgentsStore = create<AgentsState>()(
  devtools(
    (set, get) => ({
      overview: null,
      agents: [],
      issues: [],
      prs: [],
      emails: [],
      stateMd: null,
      config: null,
      cycleLogs: {},
      findingsDocs: {},
      loading: false,
      error: null,
      lastRefreshedAt: null,
      cycleLogLoading: {},
      findingsLoading: {},

      loadAll: async () => {
        if (get().loading) return;
        // Clear lazy slices so per-agent logs + findings docs refetch on the
        // next expand. The guard `if (cycleLogs[name]) return` in
        // loadCycleLog would otherwise keep stale entries forever after
        // the first load (Refresh button would silently no-op for expanded
        // panels).
        set(
          {
            loading: true,
            error: null,
            cycleLogs: {},
            findingsDocs: {},
            cycleLogLoading: {},
            findingsLoading: {},
          },
          false,
          'loadAll:start'
        );

        const results = await Promise.allSettled([
          apiService.getAgentsOverview(),
          apiService.getAgentsList(),
          apiService.getAgentsOpenIssues(),
          apiService.getAgentsRecentPRs(),
          apiService.getAgentsRecentEmails(),
          apiService.getAgentsStateMd(),
          apiService.getAgentsConfig(),
        ]);

        const next: Partial<AgentsState> = {
          loading: false,
          lastRefreshedAt: new Date().toISOString(),
        };
        const failures: string[] = [];

        if (results[0].status === 'fulfilled') next.overview = results[0].value;
        else failures.push(`overview (${results[0].reason})`);

        if (results[1].status === 'fulfilled') next.agents = results[1].value;
        else failures.push(`agents (${results[1].reason})`);

        if (results[2].status === 'fulfilled') next.issues = results[2].value;
        else failures.push(`issues (${results[2].reason})`);

        if (results[3].status === 'fulfilled') next.prs = results[3].value;
        else failures.push(`prs (${results[3].reason})`);

        if (results[4].status === 'fulfilled') next.emails = results[4].value;
        else failures.push(`emails (${results[4].reason})`);

        if (results[5].status === 'fulfilled') next.stateMd = results[5].value;
        else failures.push(`state-md (${results[5].reason})`);

        if (results[6].status === 'fulfilled') next.config = results[6].value;
        else failures.push(`config (${results[6].reason})`);

        if (failures.length > 0) {
          next.error = `Failed sections: ${failures.join(', ')}`;
        }

        set(next, false, 'loadAll:done');
      },

      refresh: async () => {
        await get().loadAll();
      },

      loadCycleLog: async (name: string) => {
        const { cycleLogLoading, cycleLogs } = get();
        if (cycleLogLoading[name] || cycleLogs[name]) return;

        set(
          { cycleLogLoading: { ...cycleLogLoading, [name]: true } },
          false,
          `loadCycleLog:${name}:start`
        );

        try {
          const entries = await apiService.getAgentCycleLog(name);
          set(
            (state) => ({
              cycleLogs: { ...state.cycleLogs, [name]: entries },
              cycleLogLoading: { ...state.cycleLogLoading, [name]: false },
            }),
            false,
            `loadCycleLog:${name}:done`
          );
        } catch (err) {
          console.error(`Failed to load cycle log for ${name}:`, err);
          set(
            (state) => ({
              cycleLogLoading: { ...state.cycleLogLoading, [name]: false },
            }),
            false,
            `loadCycleLog:${name}:error`
          );
        }
      },

      loadFindingsDoc: async (filename: string) => {
        const { findingsLoading, findingsDocs } = get();
        if (findingsLoading[filename] || findingsDocs[filename]) return;

        set(
          { findingsLoading: { ...findingsLoading, [filename]: true } },
          false,
          `loadFindingsDoc:${filename}:start`
        );

        try {
          const doc = await apiService.getFindingsDoc(filename);
          set(
            (state) => ({
              findingsDocs: { ...state.findingsDocs, [filename]: doc },
              findingsLoading: { ...state.findingsLoading, [filename]: false },
            }),
            false,
            `loadFindingsDoc:${filename}:done`
          );
        } catch (err) {
          console.error(`Failed to load findings doc ${filename}:`, err);
          set(
            (state) => ({
              findingsLoading: { ...state.findingsLoading, [filename]: false },
            }),
            false,
            `loadFindingsDoc:${filename}:error`
          );
        }
      },
    }),
    { name: 'agents-store' }
  )
);
